import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Image as ImageIcon, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "" };

export default function GalleryManager() {
  const { t } = useTranslation();
  const [albums, setAlbums] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/albums").then((r) => setAlbums(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (a) => { setEditing(a.id); setForm({ title: a.title || "", description: a.description || "" }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/albums", form);
        toast.success(t("admin.gallery.toastAlbumCreated"));
      } else {
        await api.put(`/albums/${editing}`, form);
        toast.success(t("admin.gallery.toastAlbumUpdated"));
      }
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/albums/${id}`);
      toast.success(t("admin.gallery.toastAlbumDeleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="gallery-manager" className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.gallery.eyebrow")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("admin.gallery.title")}</h1>
        </div>
        <Button onClick={openNew} data-testid="new-album-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
          <FolderPlus size={16} className="mr-2" /> {t("admin.gallery.newAlbumBtn")}
        </Button>
      </div>

      {albums.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-sm p-16 text-center text-slate-500">
          <ImageIcon className="mx-auto mb-3" size={22} />
          <p className="text-sm">{t("admin.gallery.empty")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {albums.map((a) => (
            <div key={a.id} data-testid={`album-${a.id}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden group hover:border-bordeaux transition-colors">
              <Link to={`/admin/galerie/${a.id}`} className="block">
                <div className="aspect-[4/3] bg-beige overflow-hidden">
                  {a.cover_path ? (
                    <img src={fileUrl(a.cover_path)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-400">
                      <ImageIcon size={28} />
                    </div>
                  )}
                </div>
              </Link>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/admin/galerie/${a.id}`} className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 group-hover:text-bordeaux transition-colors truncate">{a.title}</h3>
                    <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                      {t("admin.gallery.imageCount", { count: a.image_count })}
                    </p>
                  </Link>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(a)} data-testid={`album-edit-${a.id}`} className="p-2 hover:bg-slate-100 rounded-sm text-slate-600 hover:text-bordeaux">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => setConfirmId(a.id)} data-testid={`album-delete-${a.id}`} className="p-2 hover:bg-red-50 rounded-sm text-slate-600 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {a.description && <p className="text-sm text-slate-600 mt-3 line-clamp-2">{a.description}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md rounded-sm" data-testid="album-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("admin.gallery.dialogNew") : t("admin.gallery.dialogEdit")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t("admin.gallery.fieldTitle")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="album-title-input" className="rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t("admin.gallery.fieldDesc")}</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="album-desc-input" className="rounded-sm border-slate-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} className="rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900">{t("admin.common.cancel")}</Button>
            <Button onClick={save} disabled={busy} data-testid="album-save-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
              {busy ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.common.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.gallery.confirmAlbumDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="album-delete-cancel">{t("admin.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="album-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("admin.common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
